import { useState } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { useDoctors } from '@/features/appointments/useDoctors';
import { useOutsourcedWorklist, useReferenceLabs, useLabTests } from './hooks';
import { LabStatusBadge, PriorityBadge, fmtDate } from './ui';
import OutsideLabItemDialog from './OutsideLabItemDialog';
import type { OutsourcedQueue, OutsourcedRow } from './types';

/**
 * Outside / Reference Laboratory — the worklist for tests this hospital draws
 * but a reference laboratory runs.
 *
 * One row per TEST, because a report comes back per test. Every filter is
 * SERVER-side (routes/laboratory/outsourced.js), for the same reason the order
 * queue's are: filtering a fetched page would silently search only what is on
 * screen, which at a busy dispatch desk reads as "that report isn't here".
 *
 * The queues are the desk's own words; the server maps them to statuses, so
 * this screen never hardcodes a status list that could drift.
 */

const QUEUES: Array<{ id: OutsourcedQueue | 'all'; label: string }> = [
  { id: 'all',                  label: 'All' },
  { id: 'to_collect',           label: 'Ordered' },
  { id: 'to_send',              label: 'To send' },
  { id: 'awaiting_report',      label: 'Awaiting report' },
  { id: 'overdue',              label: 'Overdue' },
  { id: 'to_transcribe',        label: 'Report uploaded' },
  { id: 'pending_verification', label: 'Pending verification' },
  { id: 'to_finalize',          label: 'Verified' },
  { id: 'finalized',            label: 'Finalized' },
  { id: 'cancelled',            label: 'Cancelled' },
];

export interface OutsideLabTabProps {
  /** Filters handed over from a dashboard tile click. */
  initialFilter?: Record<string, string>;
}

export default function OutsideLabTab({ initialFilter }: OutsideLabTabProps) {
  const [queue, setQueue] = useState<OutsourcedQueue | 'all'>((initialFilter?.queue as OutsourcedQueue) ?? 'all');
  const [q, setQ] = useState('');
  const [referenceLabId, setReferenceLabId] = useState<number | ''>('');
  const [testId, setTestId] = useState<number | ''>('');
  const [doctorId, setDoctorId] = useState<number | ''>('');
  const [dateField, setDateField] = useState<'ordered' | 'sent' | 'received'>('ordered');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [openItem, setOpenItem] = useState<OutsourcedRow | null>(null);

  const labs = useReferenceLabs(true);
  const tests = useLabTests({ processing_type: 'outsourced', include_inactive: true });
  const doctors = useDoctors();

  const dateParams = dateField === 'sent'
    ? { sent_from: from || undefined, sent_to: to || undefined }
    : dateField === 'received'
      ? { received_from: from || undefined, received_to: to || undefined }
      : { from: from || undefined, to: to || undefined };

  const { data, isLoading, error, refetch, isFetching } = useOutsourcedWorklist({
    queue: queue === 'all' ? undefined : queue,
    q: q.trim() || undefined,
    reference_lab_id: referenceLabId || undefined,
    test_id: testId || undefined,
    doctor_id: doctorId || undefined,
    ...dateParams,
    page,
  });
  const rows = data?.data ?? [];
  const reset = () => setPage(1);
  const selectCls = 'h-9 rounded-lg border border-border bg-card px-2 text-[13px]';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {QUEUES.map((qq) => (
          <button
            key={qq.id}
            type="button"
            onClick={() => { setQueue(qq.id); reset(); }}
            className={`rounded-full px-3 py-1 text-[12px] ${
              queue === qq.id
                ? `${qq.id === 'overdue' ? 'bg-danger' : 'bg-teal'} font-semibold text-white`
                : 'border border-border hover:bg-muted'
            }`}
          >
            {qq.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Input className="w-[260px]" placeholder="Patient, MRN, mobile, order or accession no…" value={q}
          onChange={(e) => { setQ(e.target.value); reset(); }} />
        <select className={selectCls} value={referenceLabId} aria-label="Reference laboratory"
          onChange={(e) => { setReferenceLabId(e.target.value ? Number(e.target.value) : ''); reset(); }}>
          <option value="">All reference labs</option>
          {(labs.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}{l.active ? '' : ' (inactive)'}</option>)}
        </select>
        <select className={selectCls} value={testId} aria-label="Test"
          onChange={(e) => { setTestId(e.target.value ? Number(e.target.value) : ''); reset(); }}>
          <option value="">All outside tests</option>
          {(tests.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className={selectCls} value={doctorId} aria-label="Ordering doctor"
          onChange={(e) => { setDoctorId(e.target.value ? Number(e.target.value) : ''); reset(); }}>
          <option value="">All doctors</option>
          {(doctors.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
        </select>
        <select className={selectCls} value={dateField} aria-label="Date filter applies to"
          onChange={(e) => { setDateField(e.target.value as typeof dateField); reset(); }}>
          <option value="ordered">Order date</option>
          <option value="sent">Sample sent date</option>
          <option value="received">Report received date</option>
        </select>
        <Input type="date" className="w-[150px]" value={from} aria-label="From" onChange={(e) => { setFrom(e.target.value); reset(); }} />
        <Input type="date" className="w-[150px]" value={to} aria-label="To" onChange={(e) => { setTo(e.target.value); reset(); }} />
      </div>

      {isLoading && <LoadingState label="Loading outside laboratory tests…" />}
      {error && <ErrorState message={(error as Error).message} onRetry={() => refetch()} />}
      {!isLoading && !error && rows.length === 0 && (
        <EmptyState label="No outside laboratory tests match." hint="Tests configured as Outside / Reference Lab appear here once ordered." />
      )}

      {rows.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lab order</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Test</TableHead>
                  <TableHead>Reference lab</TableHead>
                  <TableHead>Sample</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Accession no.</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.order_item_id} className="cursor-pointer" onClick={() => setOpenItem(r)}>
                    <TableCell>
                      <div className="font-mono text-[12px]">{r.order_number}</div>
                      <div className="text-[11px] text-muted-foreground">{fmtDate(r.ordered_at)} <PriorityBadge priority={r.priority} /></div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{r.patient_name}</div>
                      <div className="text-[11px] text-muted-foreground">{r.uhid}{r.ordering_doctor_name ? ` · ${r.ordering_doctor_name}` : ''}</div>
                    </TableCell>
                    <TableCell>
                      <div>{r.test_name}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">{r.test_code}</div>
                    </TableCell>
                    <TableCell className="text-[12px]">{r.reference_lab_name ?? '—'}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">
                      {r.collected_at ? fmtDate(r.collected_at) : '—'}
                      {r.barcode && <div className="font-mono">{r.barcode}</div>}
                    </TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">
                      {r.sent_at ? fmtDate(r.sent_at) : '—'}
                      {r.expected_report_at && !r.report_id && (
                        <div className={r.overdue ? 'font-semibold text-danger-ink' : undefined}>
                          {r.overdue ? 'Overdue — due ' : 'Due '}{fmtDate(r.expected_report_at)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-[12px]">{r.external_accession_number ?? '—'}</TableCell>
                    <TableCell>
                      <LabStatusBadge status={r.status} processingType="outsourced" />
                      {r.sent_back_reason && r.status === 'REPORT_UPLOADED' && <Badge variant="warn" className="ml-1">Sent back</Badge>}
                    </TableCell>
                    <TableCell className="text-[11px]">
                      {r.report_id
                        ? <Badge variant="success">{r.result_mode === 'report_only' ? 'Report only' : 'Attached'}</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="link" size="sm" onClick={(e) => { e.stopPropagation(); setOpenItem(r); }}>
                        {nextActionLabel(r)}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-[12px] text-muted-foreground">
            <span>{data?.total ?? rows.length} test{(data?.total ?? rows.length) === 1 ? '' : 's'}{isFetching && ' · refreshing…'}</span>
            <span className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span>Page {page}</span>
              <Button variant="ghost" size="sm" disabled={!data?.has_more} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </span>
          </div>
        </>
      )}

      <OutsideLabItemDialog
        orderItemId={openItem?.order_item_id ?? null}
        onClose={() => setOpenItem(null)}
      />
    </div>
  );
}

/** What the row's button offers — the next thing that happens to this test. */
function nextActionLabel(r: OutsourcedRow): string {
  switch (r.status) {
    case 'PENDING_COLLECTION': return 'View';
    case 'COLLECTED': return 'Send';
    case 'SENT_TO_REFERENCE_LAB':
    case 'AWAITING_REPORT':
    case 'REPORT_RECEIVED': return 'Upload report';
    case 'REPORT_UPLOADED': return 'Enter result';
    case 'RESULT_ENTERED': return 'Verify';
    case 'VERIFIED': return 'Finalize';
    case 'REPORTED': return 'View report';
    default: return 'View';
  }
}
